import type {Page} from '@/types';

import {
  useExitIntent,
  ExitIntentLayerPicture as ExitIntentLayerPictureUI,
} from '@travel-frontend/ui-components';
import {useState} from 'react';

import {useConfig} from '@/modules/common/context/Config';
import {useOmnichannelChat} from '@/modules/common/context/OmnichannelChat';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useVirtualQ} from '@/modules/common/hooks/useVirtualQ';

const isVirtualQWidgetFetchedByPage: Record<Page, boolean> = {
  hotels: false,
  offers: false,
};
const omnichannelChatSourceByPage: Record<Page, string> = {
  hotels: 'Hotelliste ExitLayer',
  offers: 'Angebotliste ExitLayer',
};

type Props = {
  hotline: string;
  virtualQWidgetUrl?: string;
};

export const ExitIntentLayerPicture: React.FunctionComponent<Props> = ({
  hotline,
  virtualQWidgetUrl,
}) => {
  const config = useConfig();
  const currentPage = useCurrentPage();
  const virtualQ = useVirtualQ();
  const [omnichannelChat] = useOmnichannelChat();
  const [open, setOpen] = useState(false);

  const openVirtualQWidget = async () => {
    if (
      virtualQWidgetUrl &&
      !isVirtualQWidgetFetchedByPage[currentPage] &&
      !virtualQ.isLoading
    ) {
      const widget = await virtualQ.loadWidget(virtualQWidgetUrl);
      if (widget !== null) {
        isVirtualQWidgetFetchedByPage[currentPage] = true;
        widget.toggleWidget();
        return true;
      }
    }
    return false;
  };
  const openOmnichannelChat = () => {
    if (omnichannelChat) {
      omnichannelChat.openWidget({
        customContext: {
          // variables names are passed through "as is", so these names are what the service center agent sees
          Herkunft: {
            isDisplayable: true,
            Value: omnichannelChatSourceByPage[currentPage],
          },
          Url: {
            isDisplayable: true,
            Value: window.location.href,
          },
        },
      });
      return true;
    }
    return false;
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleChatButtonClick = () => {
    if (openOmnichannelChat()) {
      setOpen(false);
    }
  };
  const handleCallbackButtonClick = async () => {
    if (await openVirtualQWidget()) {
      setOpen(false);
    }
  };

  useExitIntent({
    enabled: true,
    delay: 15_000,
    optOutCookieName: 'exit-layer-opt-out',
    optOutCookieExpiryDays: 1,
    onExitIntent: () => {
      setOpen(true);
    },
  });

  return (
    <ExitIntentLayerPictureUI
      open={open}
      hotline={hotline}
      withChatButton={Boolean(omnichannelChat?.isVisible)}
      withHotlineButton={Boolean(hotline)}
      withCallbackButton={config.virtualQ.enabled}
      onClose={handleClose}
      onClickChatButton={handleChatButtonClick}
      onClickCallbackButton={handleCallbackButtonClick}
    />
  );
};
